import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { api } from '../../services/api'

// Fetch user profile
export const fetchUserProfile = createAsyncThunk(
  'user/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/users/profile')
      return response.data.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile')
    }
  }
)

// Update user profile
export const updateUserProfile = createAsyncThunk(
  'user/updateUserProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await api.put('/users/profile', profileData)
      return response.data.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile')
    }
  }
)

// Fetch user points balance
export const fetchPointsBalance = createAsyncThunk(
  'user/fetchPointsBalance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/users/points')
      return response.data.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch points')
    }
  }
)

// Fetch points history
export const fetchPointsHistory = createAsyncThunk(
  'user/fetchPointsHistory',
  async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/users/points/history', {
        params: { page, limit }
      })
      return response.data.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch points history')
    }
  }
)

// Change password
export const changePassword = createAsyncThunk(
  'user/changePassword',
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await api.put('/users/change-password', {
        currentPassword,
        newPassword
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change password')
    }
  }
)

const userSlice = createSlice({
  name: 'user',
  initialState: {
    profile: null,
    points: 0,
    pointsHistory: [],
    pointsHistoryPagination: {
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      hasNext: false,
      hasPrev: false,
    },
    loading: false,
    error: null,
    updateSuccess: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearUpdateSuccess: (state) => {
      state.updateSuccess = false
    },
    updateLocalPoints: (state, action) => {
      state.points = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch User Profile
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false
        state.profile = action.payload
        state.points = action.payload.points || 0
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Update User Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true
        state.error = null
        state.updateSuccess = false
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false
        state.profile = action.payload
        state.updateSuccess = true
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Fetch Points Balance
      .addCase(fetchPointsBalance.pending, (state) => {
        state.error = null
      })
      .addCase(fetchPointsBalance.fulfilled, (state, action) => {
        state.points = action.payload.pointsBalance || 0
      })
      .addCase(fetchPointsBalance.rejected, (state, action) => {
        state.error = action.payload
      })
      // Fetch Points History
      .addCase(fetchPointsHistory.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPointsHistory.fulfilled, (state, action) => {
        state.loading = false
        state.pointsHistory = action.payload.pointsHistory
        state.pointsHistoryPagination = action.payload.pagination
      })
      .addCase(fetchPointsHistory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true
        state.error = null
        state.updateSuccess = false
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false
        state.updateSuccess = true
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { clearError, clearUpdateSuccess, updateLocalPoints } = userSlice.actions
export default userSlice.reducer
